import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import NumberFormat from "react-number-format";
import {PlusIcon} from "@heroicons/react/outline";

import SlidePanel from "../../../components/layout/slide-panel";
import ListSection from "../../../components/layout/list-section";
import ProductCell from "../../../components/common/table/product-cell";
import TableDetail from "../../../components/common/list/table-detail";
import CardDetail from "../../../components/common/list/card-detail";
import DynamicFilterMenu from "../../../components/common/table/dynamic-filter-menu";
import {StatusBarIcon} from "../../../components/common/alert/status-bar-icon";
import {generateParamsFormLocation} from "../../../constants/dynamic-filter";
import {getProductsFilter} from "../../../api/catalog";
import {getQuerySearchParam} from "../../../utils/converter";
import {
    DOCUMENT_PANEL_OVERLAY_Z_INDEX_CLASS,
    DOCUMENT_PANEL_Z_INDEX_CLASS,
} from "../constants";

const DEFAULT_LIMIT = 25;

const DocumentBodyProductAdvancedSearch = ({
    isOpen,
    onClose,
    onSelect,
    companyId,
    excludeProductIds,
}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const {company} = useSelector((state) => state.userCompany ?? {});

    const [query, setQuery] = useState(getQuerySearchParam());
    const [offset, setOffset] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [dynamicFilters, setDynamicFilters] = useState([]);
    const [activeDynamicFilter, setActiveDynamicFilter] = useState(() => generateParamsFormLocation(location.search));
    const [defaultParamsUsed, setDefaultParamsUsed] = useState(false);
    const [defaultParams, setDefaultParams] = useState("");


    const columns = useMemo(() => ([
        t("app.container.product"),
        t("app.products.manufacture_code"),
        t("app.products.list_price"),
        (company?.product_manage_status ?? false) && t("app.products.status"),
    ].filter(Boolean)), [company?.product_manage_status, t]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        setSelectedProduct(null);
        setOffset(0);
        setQuery(getQuerySearchParam());
        setActiveDynamicFilter(generateParamsFormLocation(location.search));
        setDefaultParams("");
        setDefaultParamsUsed(false);
        return undefined;
    }, [isOpen, location.search]);

    useEffect(() => {
        if (!isOpen || !companyId) {
            return undefined;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await getProductsFilter({
                    company_id: companyId,
                    params: {
                        limit: DEFAULT_LIMIT,
                        offset: query.length > 0 ? 0 : offset,
                        query,
                    },
                    data: activeDynamicFilter,
                    signal,
                });

                if (response?.filters) {
                    setDynamicFilters(response.filters);
                }

                if (
                    response?.default_filter &&
                    !defaultParamsUsed &&
                    (!Array.isArray(activeDynamicFilter) || activeDynamicFilter.length === 0)
                ) {
                    setDefaultParams(`?${response.default_filter}`);
                    setDefaultParamsUsed(true);
                }

                if (query.length > 0 || offset === 0) {
                    const nextProducts = Array.isArray(response?.products) ? response.products : [];
                    setProducts(nextProducts);
                    setTotalRows(response?.total_rows ?? nextProducts.length);
                } else {
                    const next = Array.isArray(response?.products) ? response.products : [];
                    let updatedProducts = [];
                    setProducts((previous) => {
                        const prevList = Array.isArray(previous) ? previous : [];
                        updatedProducts = [...prevList, ...next];
                        return updatedProducts;
                    });
                    setTotalRows(response?.total_rows ?? updatedProducts.length);
                }
            } catch (error) {
                if (!signal.aborted) {
                    setProducts([]);
                    setTotalRows(0);
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchProducts();

        return () => {
            controller.abort();
        };
    }, [activeDynamicFilter, companyId, defaultParamsUsed, isOpen, offset, query]);

    const handleFetchMore = () => {
        setOffset((previous) => previous + DEFAULT_LIMIT);
    };

    const handleSearch = (value) => {
        setOffset(0);
        setQuery(value);
    };

    const handleConfirmSelection = () => {
        if (!selectedProduct || typeof onSelect !== "function") {
            return;
        }
        onSelect(selectedProduct);
        if (typeof onClose === "function") {
            onClose(false);
        }
    };


    return (
        <SlidePanel
            isOpen={isOpen}
            setIsOpen={onClose}
            size="max-w-5xl"
            panelZIndexClass={DOCUMENT_PANEL_Z_INDEX_CLASS}
            overlayZIndexClass={DOCUMENT_PANEL_OVERLAY_Z_INDEX_CLASS}
            title={t("app.documents.search_catalog", {defaultValue: "Ricerca avanzata prodotti"})}
            bg="bg-white"
            hasDivider={false}
            paddingStyle={"p-0"}
            setClean={() => {}}
        >
            <div className="space-y-6 pt-6 pb-5">
                <ListSection
                    title={t("app.products.products")}
                    columns={columns}
                    totalRows={totalRows}
                    list={products}
                    onSearch={handleSearch}
                    isLoading={(offset > 0 && query.length === 0) ? false : isLoading}
                    mobileRow={ProductMobRow}
                    desktopRow={ProductDeskRow}
                    isDynamicSearch
                    hasCheckbox={false}
                    activeDynamicFilter={true}
                    onInfiniteScroll={handleFetchMore}
                    isPanelScroll
                    extras={{
                        selectedProduct,
                        onSelect: setSelectedProduct,
                        company,
                    }}
                >
                    <DynamicFilterMenu
                        filters={dynamicFilters}
                        isLoading={isLoading && offset === 0}
                        defaultParams={defaultParams}
                        onChange={(filters) => {
                            setActiveDynamicFilter(filters);
                            setOffset(0);
                            setQuery(getQuerySearchParam());
                        }}
                    />

                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleConfirmSelection}
                            disabled={!selectedProduct}
                            className="inline-flex items-center gap-2 rounded-md bg-blue-1000 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                            <span>{t("app.common.add", {defaultValue: "Aggiungi"})}</span>
                        </button>
                    </div>
                </ListSection>
            </div>
        </SlidePanel>
    );
};

const ProductMobRow = ({element, extras}) => {
    const {t} = useTranslation();
    const {selectedProduct, onSelect, company} = extras;
    const isSelected = selectedProduct?.id === element.id;

    return (
        <li
            key={element.id}
            onClick={() => onSelect(element)}
            className={`flex cursor-pointer flex-col gap-2 p-4 ${isSelected ? "bg-indigo-50" : ""}`}
        >
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <ProductCell product={element} isActiveImage isActiveManufacture={false} />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <CardDetail title={t("app.products.manufacture_code")}>
                    {element?.manufacture_reference ?? "-"}
                </CardDetail>
                <CardDetail title={t("app.products.list_price")}>
                    <NumberFormat
                        value={element?.list_price ?? 0}
                        displayType="text"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        suffix={` ${(element?.currency_symbol ?? "").trim()}`}
                    />
                </CardDetail>
                {company?.product_manage_status && (
                    <CardDetail title={t("app.products.status")}>
                        <StatusBarIcon status={element?.status} />
                    </CardDetail>
                )}
            </div>
        </li>
    );
};

const ProductDeskRow = ({element, extras}) => {
    const {selectedProduct, onSelect, company} = extras;
    const isSelected = selectedProduct?.id === element.id;
    const currencySymbol = (element?.currency_symbol ?? "").trim();

    return (
        <tr
            key={element.id}
            onClick={() => onSelect(element)}
            className={`cursor-pointer ${isSelected ? "bg-indigo-50" : ""}`}
        >
            <TableDetail extraClass="whitespace-pre-line">
                <ProductCell product={element} isActiveImage isActiveManufacture={false} />
            </TableDetail>
            <TableDetail>
                <div className="text-sm text-gray-900">
                    {element?.manufacture_reference ?? "-"}
                </div>
                <div className="text-xs text-gray-700">
                    {element?.company_manufacture?.name ?? ""}
                </div>
            </TableDetail>
            <TableDetail>
                <NumberFormat
                    value={element?.list_price ?? 0}
                    displayType="text"
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    suffix={` ${currencySymbol}`}
                />
            </TableDetail>
            {company?.product_manage_status && (
                <TableDetail>
                    <StatusBarIcon status={element?.status} />
                </TableDetail>
            )}
        </tr>
    );
};

DocumentBodyProductAdvancedSearch.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    excludeProductIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

DocumentBodyProductAdvancedSearch.defaultProps = {
    companyId: null,
    excludeProductIds: [],
};

ProductMobRow.propTypes = {
    element: PropTypes.object.isRequired,
    extras: PropTypes.shape({
        selectedProduct: PropTypes.object,
        onSelect: PropTypes.func.isRequired,
        company: PropTypes.object,
    }).isRequired,
};

ProductDeskRow.propTypes = {
    element: PropTypes.object.isRequired,
    extras: PropTypes.shape({
        selectedProduct: PropTypes.object,
        onSelect: PropTypes.func.isRequired,
        company: PropTypes.object,
    }).isRequired,
};

export default DocumentBodyProductAdvancedSearch;
